import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, InputNumber, Tooltip } from 'antd';
import AlertBox from '@/components/alert-box';
import { ToggleHeader, ToggleSwitch } from '@/components/toggle-switch';
import { DATE_FORMATS } from '@/utils/formUtils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

/** HR rules: documents, holiday tracker and loyalty days. */
export default function CompanyHrRulesFields() {
  const {
    foreignWorkPermitTrackerKeys,
    foreignWorkPermitTrackerState,
    form,
    holidayDaysTrackerChecked,
    holidayDaysTrackerKeys,
    holidayDaysTrackerState,
    isEdited,
    isHrChecked,
    isHrOfficerSelfApproveKeys,
    isHrOfficerSelfApproveState,
    isProfile,
    isSectorChecked,
    isSectorKeys,
    isSectorState,
    loyaltyEnabled,
    sectionClass,
    strictOldHolidayCutoffKeys,
    strictOldHolidayCutoffState,
    holidayFirstPartRequiredKeys,
    holidayFirstPartRequiredState,
    holidayFirstPartWarningsKeys,
    holidayFirstPartWarningsState,
    t,
    trackerStartDateKeys,
    trackerStartDateState,
  } = useCompanyGeneralDataForm();
  // On the profile: laid out by CompanyHrProfilePanel.
  if (isProfile) return null;

  return (
    <>
      {!isProfile && <hr className="mb-5" />}

      <div className={sectionClass('hr')}>
        {isHrChecked && (
          <>
            {isSectorState?.visible && (
              <ToggleHeader
                title={t('sectors', { ns: 'forms' })}
                className="mb-5"
              >
                <ToggleSwitch
                  form={form}
                  name={isSectorKeys}
                  disabled={isSectorState?.disabled}
                />
              </ToggleHeader>
            )}

            {isSectorChecked && isHrOfficerSelfApproveState?.visible && (
              <div className="mb-5 rounded-md border p-4">
                <AlertBox variant="info" className="z-[1]">
                  {t('questionnaire.hrOfficerSelfApproveInfo', { ns: 'forms' })}
                </AlertBox>

                <ToggleHeader
                  title={t('questionnaire.hrOfficerSelfApprove', {
                    ns: 'forms',
                  })}
                >
                  <ToggleSwitch
                    form={form}
                    name={isHrOfficerSelfApproveKeys}
                    disabled={isHrOfficerSelfApproveState?.disabled}
                  />
                </ToggleHeader>
              </div>
            )}

            {!isProfile && <hr className="mb-5" />}
          </>
        )}

        {foreignWorkPermitTrackerState?.visible && (
          <ToggleHeader
            title={t('foreignWorkPermitTracker', { ns: 'forms' })}
            className="mb-5"
          >
            <ToggleSwitch
              name={foreignWorkPermitTrackerKeys}
              form={form}
              disabled={foreignWorkPermitTrackerState?.disabled}
            />
          </ToggleHeader>
        )}

        {isHrChecked && (
          <>
            {holidayDaysTrackerState?.visible && (
              <>
                {!isProfile && <hr className="mb-5" />}

                <ToggleHeader
                  className="mb-5"
                  title={t('holidayDaysTracker', { ns: 'forms' })}
                >
                  <ToggleSwitch
                    form={form}
                    name={holidayDaysTrackerKeys}
                    disabled={holidayDaysTrackerState?.disabled}
                  />
                </ToggleHeader>
              </>
            )}
          </>
        )}

        {isHrChecked && holidayDaysTrackerChecked && (
          <>
            {trackerStartDateState?.visible && (
              <Form.Item
                name={trackerStartDateKeys}
                label={t('startDateOfWork', { ns: 'forms' })}
                rules={[
                  {
                    required: true,
                    message: t('formValidation.selectDateRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <DatePicker
                  className="w-full"
                  format={DATE_FORMATS.DISPLAY}
                  placeholder={t('startDateOfWork', { ns: 'forms' })}
                  disabled={trackerStartDateState?.disabled}
                />
              </Form.Item>
            )}

            {strictOldHolidayCutoffState?.visible && (
              <ToggleHeader
                className="mb-5"
                title={
                  <>
                    {t('strictOldHolidayCutoff', { ns: 'forms' })}
                    <Tooltip
                      placement="topLeft"
                      title={t('strictOldHolidayCutoffTooltip', {
                        ns: 'forms',
                      })}
                    >
                      <InfoCircleOutlined className="ml-2 text-base text-zinc-400" />
                    </Tooltip>
                  </>
                }
              >
                <ToggleSwitch
                  form={form}
                  name={strictOldHolidayCutoffKeys}
                  disabled={strictOldHolidayCutoffState?.disabled}
                />
              </ToggleHeader>
            )}

            {holidayFirstPartRequiredState?.visible && (
              <ToggleHeader
                className="mb-5"
                title={
                  <>
                    {t('holidayFirstPartRequired', { ns: 'forms' })}
                    <Tooltip
                      placement="topLeft"
                      title={t('holidayFirstPartRequiredTooltip', {
                        ns: 'forms',
                      })}
                    >
                      <InfoCircleOutlined className="ml-2 text-base text-zinc-400" />
                    </Tooltip>
                  </>
                }
              >
                <ToggleSwitch
                  form={form}
                  name={holidayFirstPartRequiredKeys}
                  disabled={holidayFirstPartRequiredState?.disabled}
                />
              </ToggleHeader>
            )}

            {holidayFirstPartWarningsState?.visible && (
              <ToggleHeader
                className="mb-5"
                title={
                  <>
                    {t('holidayFirstPartWarnings', { ns: 'forms' })}
                    <Tooltip
                      placement="topLeft"
                      title={t('holidayFirstPartWarningsTooltip', {
                        ns: 'forms',
                      })}
                    >
                      <InfoCircleOutlined className="ml-2 text-base text-zinc-400" />
                    </Tooltip>
                  </>
                }
              >
                <ToggleSwitch
                  form={form}
                  name={holidayFirstPartWarningsKeys}
                  disabled={holidayFirstPartWarningsState?.disabled}
                />
              </ToggleHeader>
            )}
          </>
        )}

        {isHrChecked && (
          <>
            {!isProfile && <hr className="mb-5" />}

            <ToggleHeader
              className="mb-5"
              title={t('loyaltyHolidaySettings', { ns: 'messages' })}
            >
              <ToggleSwitch
                form={form}
                name={['loyaltyHolidaySettings', 'enabled']}
                disabled={!isEdited}
              />
            </ToggleHeader>

            {loyaltyEnabled && (
              <Form.List name={['loyaltyHolidaySettings', 'thresholds']}>
                {(fields, { add, remove }) => (
                  <div className="mb-4">
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="mb-2 flex items-center gap-2">
                        <span className="text-sm">
                          {t('afterYears', { ns: 'messages' })}
                        </span>
                        <Form.Item
                          {...restField}
                          name={[name, 'yearsOfService']}
                          className="!mb-0"
                          rules={[{ required: true }]}
                        >
                          <InputNumber
                            min={1}
                            max={50}
                            disabled={!isEdited}
                            className="w-16"
                          />
                        </Form.Item>
                        <span className="text-sm">→ +</span>
                        <Form.Item
                          {...restField}
                          name={[name, 'additionalDays']}
                          className="!mb-0"
                          rules={[{ required: true }]}
                        >
                          <InputNumber
                            min={1}
                            max={30}
                            disabled={!isEdited}
                            className="w-16"
                          />
                        </Form.Item>
                        <span className="text-sm">
                          {t('daysLabel', { ns: 'messages' })}
                        </span>
                        {isEdited && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(name)}
                            className="!p-0"
                          >
                            ✕
                          </Button>
                        )}
                      </div>
                    ))}
                    {isEdited && (
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({ yearsOfService: 1, additionalDays: 1 })
                        }
                        className="mt-2"
                      >
                        + {t('addThreshold', { ns: 'messages' })}
                      </Button>
                    )}
                  </div>
                )}
              </Form.List>
            )}
          </>
        )}
      </div>
    </>
  );
}
